import { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import type {
    NotificationChannel,
    NotificationSubscriptionUpdate,
    ProjectNotificationRecipient,
} from '../../api/notificationSubscriptions';
export interface RecipientKind {
    kind: string;
    label: string;
    icon: JSX.Element;
}

export interface ProjectRecipientsTableProps {
    users: ProjectNotificationRecipient[];
    /** Kinds the organisation can actually use — a column of permanently
     *  disabled switches explains nothing anyone here can act on. */
    kinds: RecipientKind[];
    /** Tenant admins edit every row; everyone else only their own. */
    canManage: boolean;
    currentUserId?: number;
    onUpdate: (userId: number, update: NotificationSubscriptionUpdate) => void;
}

const SNOOZE_HOURS = [12, 24, 48];

const CHANNELS: { channel: NotificationChannel; label: string }[] = [
    { channel: 'email', label: 'Email' },
    { channel: 'sms', label: 'SMS' },
];

const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#00d4ad' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#00d4ad',
    },
    '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': { color: '#0f8f77' },
    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.35,
    },
};

const headCellSx = {
    color: '#94a3b8',
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'uppercase' as const,
    backgroundColor: '#1f2338',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
    color: '#fff',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    py: 0.75,
};

const chipSx = (color: string) => ({
    height: 18,
    fontSize: '0.6rem',
    fontWeight: 600,
    color,
    border: `1px solid ${color}`,
    backgroundColor: 'transparent',
    '& .MuiChip-label': { px: 0.75 },
});

/**
 * Who receives this project's alerts: one row per user in the organisation,
 * one email/SMS switch pair per detection kind.
 *
 * The rules mirror the backend and the admin user page: switching ON needs
 * the kind available and (for SMS) a phone number on the account; switching
 * OFF is always allowed; only an admin may touch someone else's row. A
 * snooze silences the person who sets it, so it is offered on one's own row
 * only — an admin can unsubscribe someone, but not quietly mute them.
 */
export default function ProjectRecipientsTable({
    users,
    kinds,
    canManage,
    currentUserId,
    onUpdate,
}: ProjectRecipientsTableProps) {
    const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);

    const isOn = (user: ProjectNotificationRecipient, kind: string) => {
        const channels = user.subscriptions[kind]?.channels;
        return channels?.email === true || channels?.sms === true;
    };
    const rowFullyOn = (user: ProjectNotificationRecipient) =>
        kinds.every(
            ({ kind }) => user.subscriptions[kind]?.channels?.email === true
        );

    // Snoozing applies to every kind the person is subscribed to: one
    // "quiet for the weekend" action, not three.
    const activeKinds = (user: ProjectNotificationRecipient) =>
        kinds.filter(({ kind }) => isOn(user, kind));
    const snoozedKinds = (user: ProjectNotificationRecipient) =>
        kinds.filter(({ kind }) => {
            const until = user.subscriptions[kind]?.snoozed_until;
            return until && new Date(until) > new Date();
        });
    const snooze = (user: ProjectNotificationRecipient, hours: number) => {
        const targets = hours > 0 ? activeKinds(user) : snoozedKinds(user);
        setSnoozeAnchor(null);
        if (!targets.length) return;
        onUpdate(
            user.id,
            Object.fromEntries(
                targets.map(({ kind }) => [kind, { snooze_hours: hours }])
            )
        );
    };

    // Row-level subscribe/unsubscribe: email only when switching on (SMS
    // costs money per message and needs a number), both channels when
    // switching off.
    const toggleRow = (user: ProjectNotificationRecipient) => {
        const channels = rowFullyOn(user)
            ? { email: false, sms: false }
            : { email: true };
        onUpdate(
            user.id,
            Object.fromEntries(kinds.map(({ kind }) => [kind, { channels }]))
        );
    };

    return (
        <TableContainer
            sx={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.03)',
                maxHeight: 420,
            }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={headCellSx}>User</TableCell>
                        {kinds.map(({ kind, label, icon }) => (
                            <TableCell
                                key={kind}
                                align="center"
                                sx={{
                                    ...headCellSx,
                                    borderLeft: '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    {icon}
                                    {label}
                                </Box>
                            </TableCell>
                        ))}
                        <TableCell sx={headCellSx} align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((user) => {
                        const isSelf = user.id === currentUserId;
                        const editable = canManage || isSelf;
                        const hasPhone = !!user.phone_number;
                        const snoozed = snoozedKinds(user);
                        const snoozedUntil = snoozed.length
                            ? new Date(
                                  Math.max(
                                      ...snoozed.map(({ kind }) =>
                                          new Date(
                                              user.subscriptions[kind]!.snoozed_until!
                                          ).getTime()
                                      )
                                  )
                              )
                            : null;
                        return (
                            <TableRow
                                key={user.id}
                                hover
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.04)',
                                    },
                                    ...(isSelf && {
                                        backgroundColor: 'rgba(0, 212, 173, 0.05)',
                                    }),
                                }}
                            >
                                <TableCell sx={bodyCellSx}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Typography
                                            sx={{ fontSize: '0.85rem', fontWeight: 600 }}
                                        >
                                            {user.username}
                                        </Typography>
                                        {isSelf && (
                                            <Chip
                                                size="small"
                                                label="You"
                                                sx={chipSx('#00d4ad')}
                                            />
                                        )}
                                        {user.is_staff && (
                                            <Chip
                                                size="small"
                                                label="Admin"
                                                sx={chipSx('#94a3b8')}
                                            />
                                        )}
                                        {!editable && (
                                            <Tooltip
                                                title="Only an administrator can change another user's notifications"
                                            >
                                                <LockOutlinedIcon
                                                    sx={{ fontSize: 14, color: '#64748b' }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{ fontSize: '0.72rem', color: '#94a3b8' }}
                                    >
                                        {user.email || 'No email address'}
                                        {hasPhone ? ` · ${user.phone_number}` : ''}
                                    </Typography>
                                    {snoozed.length > 0 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mt: 0.25,
                                            }}
                                        >
                                            <NotificationsPausedIcon
                                                sx={{ fontSize: 13, color: '#f59e0b' }}
                                            />
                                            <Typography
                                                sx={{ fontSize: '0.68rem', color: '#f59e0b' }}
                                            >
                                                {snoozed
                                                    .map(({ label }) => label.toLowerCase())
                                                    .join(', ')}{' '}
                                                snoozed until{' '}
                                                {snoozedUntil?.toLocaleString(undefined, {
                                                    weekday: 'short',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </Typography>
                                        </Box>
                                    )}
                                </TableCell>
                                {kinds.map(({ kind, label }) => {
                                    const channels = user.subscriptions[kind]?.channels ?? {
                                        email: false,
                                        sms: false,
                                    };
                                    return (
                                        <TableCell
                                            key={kind}
                                            align="center"
                                            sx={{
                                                ...bodyCellSx,
                                                borderLeft:
                                                    '1px solid rgba(255,255,255,0.06)',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    gap: 1.5,
                                                }}
                                            >
                                                {CHANNELS.map(
                                                    ({ channel, label: channelLabel }) => {
                                                        const checked =
                                                            channels[channel] === true;
                                                        // Switching OFF stays possible
                                                        // where the gate blocks switching
                                                        // on (mirrors the backend).
                                                        const smsBlocked =
                                                            channel === 'sms' &&
                                                            !hasPhone &&
                                                            !checked;
                                                        const disabled =
                                                            !editable || smsBlocked;
                                                        const title = !editable
                                                            ? ''
                                                            : smsBlocked
                                                              ? isSelf
                                                                  ? 'Add a phone number on the My Account page to enable SMS'
                                                                  : 'No mobile number on this account, so SMS cannot be delivered'
                                                              : '';
                                                        return (
                                                            <Tooltip
                                                                key={channel}
                                                                title={title}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 0.25,
                                                                    }}
                                                                >
                                                                    <Switch
                                                                        size="small"
                                                                        checked={checked}
                                                                        disabled={disabled}
                                                                        onChange={(e) =>
                                                                            onUpdate(user.id, {
                                                                                [kind]: {
                                                                                    channels: {
                                                                                        [channel]:
                                                                                            e
                                                                                                .target
                                                                                                .checked,
                                                                                    },
                                                                                },
                                                                            })
                                                                        }
                                                                        inputProps={{
                                                                            'aria-label': `${user.username}: ${label} ${channelLabel}`,
                                                                        }}
                                                                        sx={switchSx}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize:
                                                                                '0.72rem',
                                                                            color: disabled
                                                                                ? '#64748b'
                                                                                : '#cbd5e1',
                                                                        }}
                                                                    >
                                                                        {channelLabel}
                                                                    </Typography>
                                                                </Box>
                                                            </Tooltip>
                                                        );
                                                    }
                                                )}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                                <TableCell align="right" sx={bodyCellSx}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        {isSelf && (
                                            <Tooltip
                                                title={
                                                    snoozed.length
                                                        ? 'Snoozed — resume or extend'
                                                        : activeKinds(user).length
                                                          ? 'Snooze your alerts for this project'
                                                          : 'Subscribe first to snooze'
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={
                                                            !activeKinds(user).length &&
                                                            !snoozed.length
                                                        }
                                                        onClick={(e) =>
                                                            setSnoozeAnchor(e.currentTarget)
                                                        }
                                                        aria-label="Snooze your alerts"
                                                        sx={{
                                                            color: snoozed.length
                                                                ? '#f59e0b'
                                                                : '#94a3b8',
                                                        }}
                                                    >
                                                        {snoozed.length ? (
                                                            <NotificationsPausedIcon
                                                                sx={{ fontSize: 18 }}
                                                            />
                                                        ) : (
                                                            <NotificationsActiveIcon
                                                                sx={{ fontSize: 18 }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {isSelf && (
                                            <Menu
                                                anchorEl={snoozeAnchor}
                                                open={!!snoozeAnchor}
                                                onClose={() => setSnoozeAnchor(null)}
                                            >
                                                {SNOOZE_HOURS.map((hours) => (
                                                    <MenuItem
                                                        key={hours}
                                                        dense
                                                        disabled={!activeKinds(user).length}
                                                        onClick={() => snooze(user, hours)}
                                                    >
                                                        Snooze for {hours} hours
                                                    </MenuItem>
                                                ))}
                                                {snoozed.length > 0 && (
                                                    <MenuItem
                                                        dense
                                                        onClick={() => snooze(user, 0)}
                                                    >
                                                        Resume now
                                                    </MenuItem>
                                                )}
                                            </Menu>
                                        )}
                                        {editable && (
                                            <Button
                                                size="small"
                                                onClick={() => toggleRow(user)}
                                                sx={{
                                                    color: '#00d4ad',
                                                    textTransform: 'none',
                                                    fontSize: '0.72rem',
                                                    minWidth: 0,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {rowFullyOn(user) ? 'None' : 'All'}
                                            </Button>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
